// client/src/pages/Game/Battlefield/components/PlayerHand.jsx

import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import './PlayerHand.css';

/** Icne il (anime : paupire ferme qui s'ouvre) affiche au-dessus de la carte slectionne.
 *  Cliquer dessus ouvre l'aperu dtaill de la carte. */
function EyeButton({ onOpen }) {
  return (
    <button
      type="button"
      className="hand-eye-button"
      aria-label="Voir le dtail de la carte"
      title="Voir le dtail"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
    >
      <svg viewBox="0 0 48 32" className="hand-eye-svg">
        <path className="hand-eye-lid" d="M4 16 Q24 -4 44 16 Q24 36 4 16 Z" />
        <circle className="hand-eye-pupil" cx="24" cy="16" r="6" />
        <rect className="hand-eye-shutter" x="0" y="0" width="48" height="32" />
      </svg>
    </button>
  );
}

/**
 * Main du joueur. Position et taille en pourcentages du frame.
 */
const PlayerHand = ({
  player,
  gameState,
  position,
  size,
  onCardClick,
  onCardDragStart,
  onDevDragStart,
  devMode,
  getCardElement
}) => {
  // ── Animation de pioche ──
  // Détecte les cartes NOUVELLES (instanceId absent du rendu précédent) et
  // les fait voler depuis le deck jusqu'à leur place : couvre la pioche de
  // début de tour, les remplacements du mulligan et toute carte gagnée.
  // (Hooks AVANT tout early return — rules of hooks)
  const prevIdsRef = useRef(null);
  const cardElsRef = useRef(new Map());

  const hand = useMemo(
    () => (player === 'player'
      ? (gameState?.playerHand ?? [])
      : (gameState?.opponentHand ?? [])),
    [gameState, player]
  );
  const isPlayerTurn = gameState?.activePlayer === 'player';

  useLayoutEffect(() => {
    const keyOf = (c) => String(c.instanceId || c.id);
    const ids = hand.map(keyOf);
    const prev = prevIdsRef.current;
    prevIdsRef.current = ids;

    if (prev && gameState) {
      const newIds = ids.filter(id => !prev.includes(id)).slice(0, 5);
      if (newIds.length > 0) {
        const deckSel = player === 'player' ? '.player-deck' : '.opponent-deck';
        const deckEl = document.querySelector(deckSel);
        const dr = deckEl?.getBoundingClientRect();
        const origin = (dr && dr.width > 0)
          ? { x: dr.left + dr.width / 2, y: dr.top + dr.height / 2 }
          : (player === 'player'
            ? { x: 90, y: window.innerHeight - 110 }
            : { x: window.innerWidth - 90, y: 110 });

        let k = 0;
        newIds.forEach(id => {
          const el = cardElsRef.current.get(id);
          if (!el) return;
          const r = el.getBoundingClientRect();
          const dx = origin.x - (r.left + r.width / 2);
          const dy = origin.y - (r.top + r.height / 2);
          const base = el.style.transform || '';
          // R34u : sauvegarder le z-index React avant l'anim et le RESTAURER
          // (l'ancien code mettait '' ce qui écrasait le style inline React
          // et laissait la carte au-dessus jusqu'à la prochaine pioche).
          const prevZ = el.style.zIndex;
          el.style.zIndex = 200;
          const anim = el.animate([
            { transform: `translate(${dx}px, ${dy}px) rotate(-14deg) scale(0.72) ${base}`, opacity: 0.2 },
            { transform: `translate(0, 0) rotate(0deg) scale(1) ${base}`, opacity: 1 },
          ], {
            duration: 520,
            delay: 130 * k++,
            easing: 'cubic-bezier(0.22, 0.9, 0.32, 1)',
            fill: 'backwards',
          });
          // FIX : sans ce reset, la carte pioche gardait zIndex 200 et
          // restait bloque AU-DESSUS des autres aprs l'animation.
          const restore = () => { el.style.zIndex = prevZ; };
          if (anim.onfinish) anim.onfinish = restore;
          else setTimeout(restore, 520 + 130 * k);
        });
      }
    }

    // Purger les refs des cartes parties
    for (const id of cardElsRef.current.keys()) {
      if (!ids.includes(id)) cardElsRef.current.delete(id);
    }
  }, [hand, player, gameState]);

  const [selectedKey, setSelectedKey] = useState(null);
  const keyOfCard = (c) => String(c.instanceId || c.id);

  if (!gameState) return null;


  const handleDevDrag = (e) => {
    if (devMode && onDevDragStart) onDevDragStart(e);
  };

  // Arrangement en éventail — R34ii : l'écart MAXIMAL entre cartes est
  // plafonné pour que 2-3 cartes restent un éventail SERRÉ (recouvrement
  // net) au lieu de s'étaler sur toute la largeur de l'élément. L'angle
  // suit le même rapport : peu de cartes = angle réduit, pas d'étalement.
  // (Avant : plafond cardW*0.62 + angle fixe 30° → 2-3 cartes très écartées.)
  const calculateCardPosition = (index, totalCards) => {
    if (totalCards <= 1) return { rotation: 0, offsetX: 0, flip: false };
    const maxAngle = 30;
    const cardW = (size.height * 2) / 3;        // cqw (aspect 2/3)
    const usable = Math.max(size.width - cardW, 1);
    // Recouvrement cible : les cartes se chevauchent d'environ 55% de leur
    // largeur quand la place ne manque pas.
    const spread = Math.min(cardW * 0.45, usable / (totalCards - 1));
    const centerIndex = (totalCards - 1) / 2;
    const relativeIndex = index - centerIndex;
    const normalizedIndex = centerIndex === 0 ? 0 : relativeIndex / centerIndex;
    // Angle proportionnel au REMPLISSAGE de la main (R34iib) :
    // 2 cartes → ±6°, 3 → ±12°, 6+ → ±30°. Peu de cartes = éventail
    // serré, plus d'étalement démesuré sur la largeur de l'élément.
    const angleRatio = Math.min(1, (totalCards - 1) / 5);
    const rotation = normalizedIndex * maxAngle * angleRatio;
    const offsetX = relativeIndex * spread;
    return {
      rotation: player === 'opponent' ? -rotation : rotation,
      offsetX,
      flip: player === 'opponent'
    };
  };

  // Transform de base d'une carte (éventail). `lifted` = surélevation
  // (hover OU sélection) : la carte sort à moitié de la main.
  const cardTransform = (index, lifted) => {
    const { rotation, offsetX, flip } = calculateCardPosition(index, hand.length);
    const lift = lifted ? ' translateY(-2.6cqw)' : '';
    // R34q : recentrage explicite — left:50% + translateX(-50%) : chaque carte
    // est ancrée à sa position calculée, plus de position statique flex floue.
    return `translateX(-50%) translateX(${offsetX}cqw)${lift} rotate(${rotation}deg) ${flip ? 'scaleX(-1)' : ''}`;
  };

  return (
    <div
      className={`game-element hand-area ${player}-hand`}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        width: `${size.width}cqw`,
        height: `${size.height}cqw`,
        transform: 'translate(-50%, -50%)',
        cursor: devMode ? 'move' : 'default',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      onMouseDown={devMode ? handleDevDrag : undefined}
      onTouchStart={devMode ? handleDevDrag : undefined}
    >
      <style>
        {`
          .hand-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          .player-hand .hand-card:hover {
            box-shadow: 0 5px 15px rgba(90, 205, 255, 0.8);
          }
          @keyframes card-draw {
            0% { transform: scale(0.1); opacity: 0; }
            50% { transform: scale(1.2); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          .card-drawn { animation: card-draw 0.5s forwards; }
          @keyframes card-destroy {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.2) rotate(5deg); opacity: 0.8; }
            100% { transform: scale(0); opacity: 0; }
          }
          .card-destroyed { animation: card-destroy 0.5s forwards; }
          @keyframes crystal-glow-pulse {
            0% { filter: brightness(0.9); box-shadow: 0 0 5px #0066cc, 0 0 10px #5ACDFF; }
            50% { filter: brightness(1.2); box-shadow: 0 0 10px #0066cc, 0 0 20px #5ACDFF; }
            100% { filter: brightness(0.9); box-shadow: 0 0 5px #0066cc, 0 0 10px #5ACDFF; }
          }
        `}
      </style>

      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: player === 'opponent' ? 'flex-start' : 'flex-end',
        overflow: 'visible',
      }}>
        {hand.map((card, index) => {
          const { rotation, offsetX, flip } = calculateCardPosition(index, hand.length);
          // R34m : la sélection ne réarrange PAS l'éventail — la carte
          // garde sa place ; seul son z-index monte (au-dessus du bouton
          // d'action z50) pour que l'œil reste visible et cliquable.
          const cardKey = keyOfCard(card);
          // R34r : z-index STRICTEMENT décroissant de gauche à droite
          // (la carte la plus à gauche couvre toujours les suivantes),
          // sélection au-dessus de tout.
          const zIndexValue = player === 'player'
            ? (selectedKey === cardKey ? 60 : (hand.length - index))
            : (hand.length - index);
          const isSelected = player === 'player' && selectedKey === cardKey;

          return (
            <div
              key={`${player}-hand-${card.instanceId || card.id}`}
              className={`hand-card${isSelected ? ' hand-card-selected' : ''}`}
              data-card-id={card.definitionId || card.id || ''}
              data-card-cost={card.cost ?? ''}
              ref={(el) => {
                const k = String(card.instanceId || card.id);
                if (el) cardElsRef.current.set(k, el);
                else cardElsRef.current.delete(k);
              }}
              style={{
                position: 'absolute',
                left: '50%',
                height: '100%',
                aspectRatio: '2 / 3',
                transform: cardTransform(index, isSelected),
                transition: 'all 0.3s ease',
                transformOrigin: player === 'opponent' ? 'top center' : 'bottom center',
                zIndex: zIndexValue,
                cursor: player === 'player' ? 'pointer' : 'default',
                overflow: 'visible',
              }}
              onMouseDown={(e) => {
                // Priorité gameplay : le drag de carte fonctionne même en mode dev.
                // stopPropagation ⇒ le drag dev (positionnement de zone) ne se déclenche pas.
                if (player === 'player' && isPlayerTurn && onCardDragStart) {
                  onCardDragStart(e, { ...card, location: 'hand' });
                }
                e.stopPropagation();
              }}
              onTouchStart={(e) => {
                if (player === 'player' && isPlayerTurn && onCardDragStart) {
                  onCardDragStart(e, { ...card, location: 'hand' });
                }
                e.stopPropagation();
              }}
              onClick={() => {
                // Sélection : surélève + lueur + œil (le détail passe par l'œil).
                // Un 2e clic désélectionne. Le drag reste géré par mousedown.
                if (player === 'player') {
                  setSelectedKey((k) => (k === cardKey ? null : cardKey));
                }
              }}
            >
              {isSelected && (
                <EyeButton onOpen={() => { if (onCardClick) onCardClick(card); }} />
              )}
              {player === 'player' ? (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img
                    src={card.imageUrl || `/api/static/images/cards/${getCardElement(card)}.svg`}
                    alt={card.name || 'Card'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '5px',
                      boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                    }}
                    onError={(e) => {
                      e.target.src = `/api/static/images/card-backs/neutral.webp`;
                    }}
                  />
                  {card.cost !== undefined && (
                    <div
                      className="card-cost-crystal"
                      style={{
                        position: 'absolute',
                        top: '-1cqw',
                        right: '-0.8cqw',
                        width: 'calc(1.8cqw * var(--gem-scale, 1))',
                        height: 'calc(2.6cqw * var(--gem-scale, 1))',
                        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                        background: 'radial-gradient(circle at 30% 30%, #80c8ff, #0066cc)',
                        border: '1px solid white',
                        color: 'white',
                        fontWeight: 'bold',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxShadow: '0 0 8px #0099ff',
                        zIndex: 50,
                        fontSize: 'calc(1.4cqw * var(--font-scale, 1) * var(--fs-cost, 1))',
                        textShadow: '0 1px 1px rgba(0, 0, 0, 0.7)',
                        animation: 'crystal-glow-pulse 3s infinite alternate ease-in-out',
                      }}
                    >
                      <div style={{
                        position: 'absolute', top: '0.4cqw', left: '0.7cqw',
                        width: '0.7cqw', height: '0.3cqw', borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        transform: 'rotate(-30deg)',
                      }} />
                      <div style={{
                        position: 'absolute', bottom: '0.6cqw', right: '0.7cqw',
                        width: '0.3cqw', height: '0.2cqw', borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        transform: 'rotate(-30deg)',
                      }} />
                      {card.cost}
                    </div>
                  )}
                </div>
              ) : (
                <img
                  src="/api/static/images/card-backs/neutral.webp"
                  alt="Card back"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '5px',
                    boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlayerHand;
